import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { dashboardAPI, commissionAPI } from '../services/api';
import StatTile from '../components/branch/StatTile';
import ActionCard from '../components/branch/ActionCard';
import { formatCurrency as fmt } from '../utils/format';

function OverdueCard({ emi }) {
  const daysOverdue = emi.nextDue
    ? Math.floor((Date.now() - new Date(emi.nextDue)) / 86400000)
    : 0;
  return (
    <div className="overdue-card">
      <div className="overdue-card-name">{emi.customerName}</div>
      <div className="overdue-card-product">{emi.productName}</div>
      <div className="overdue-card-meta">
        <span className="chip badge-overdue">{daysOverdue}d overdue</span>
        <span style={{ color: '#b45309', fontWeight: 700 }}>{fmt(emi.monthlyAmount)}/mo</span>
      </div>
      {emi.linkedCustomerId && (
        <Link to={`/customers/${emi.linkedCustomerId}`} className="overdue-card-link">
          View Profile →
        </Link>
      )}
    </div>
  );
}

const QUICK_ACTIONS = [
  { path: '/new-sale',      label: '+ New Sale',        color: '#f59e0b', textColor: '#fff' },
  { path: '/emi',           label: 'Collect EMI',        color: '#0d6efd', textColor: '#fff' },
  { path: '/analysis',      label: 'Business Analysis',  color: '#334155', textColor: '#fff' },
  { path: '/customers',     label: 'Add Customer',       color: '#7c3aed', textColor: '#fff' },
  { path: '/daily-report',  label: 'Daily Report',       color: '#198754', textColor: '#fff' },
  { path: '/cash-handover', label: 'Cash Handover',      color: '#0891b2', textColor: '#fff' },
  { path: '/commission',    label: 'My Commission',      color: '#b45309', textColor: '#fff' },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [commData, setCommData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => { load(); }, []); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [dashRes, commRes] = await Promise.all([
        dashboardAPI.get(),
        commissionAPI.timeline(1).catch(() => null)
      ]);
      setData(dashRes.data.dashboard);
      if (commRes?.data) setCommData(commRes.data);
    } catch {
      setError('Dashboard could not load. Please retry.');
    } finally {
      setLoading(false);
    }
  };

  const today = new Date();
  const dateStr = today.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const analysis = data ? {
    stockPerActiveEmi: (data.pendingEmiCollections || 0) > 0
      ? ((data.stock?.totalItems || 0) / (data.pendingEmiCollections || 1)).toFixed(1)
      : '0',
    cashFromRecoveryPct: (data.today?.totalCashToday || 0) > 0
      ? Math.round(((data.today?.emiCollectedToday || 0) / (data.today?.totalCashToday || 1)) * 100)
      : 0,
    emiRiskPct: (data.pendingEmiCollections || 0) > 0
      ? Math.round(((data.overdueEmiCount || 0) / (data.pendingEmiCollections || 1)) * 100)
      : 0
  } : null;

  return (
    <div>
      {/* Page header */}
      <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
        <div>
          <h4 className="mb-0 fw-700">Command Center</h4>
          <div className="text-muted" style={{ fontSize: 13, marginTop: 2 }}>{dateStr}</div>
        </div>
        <div className="d-flex gap-2 mt-2 mt-sm-0">
          <button className="btn btn-sm btn-outline-secondary" onClick={load} disabled={loading}>
            {loading ? 'Refreshing…' : '↻ Refresh'}
          </button>
          <button className="btn-new-sale" onClick={() => navigate('/new-sale')} style={{ padding: '8px 20px', fontSize: 14 }}>
            + New Sale
          </button>
        </div>
      </div>

      {error && (
        <div className="alert-soft warn mb-3">
          {error} <button className="btn btn-sm btn-link p-0 ms-2" onClick={load}>Retry</button>
        </div>
      )}

      {loading && !data && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" />
          <div className="text-muted mt-3">Loading dashboard…</div>
        </div>
      )}

      {data && (
        <>
          {/* ─── Today's KPI tiles ─── */}
          <div className="row g-3 mb-4">
            <div className="col-6 col-xl-2">
              <StatTile
                label="Sales today"
                value={data.today?.salesCount || 0}
                sub={`${data.today?.regularSalesCount || 0} cash · ${data.today?.emiSalesCount || 0} EMI`}
                color="#0d6efd"
                icon="🛒"
              />
            </div>
            <div className="col-6 col-xl-2">
              <StatTile
                label="Revenue today"
                value={fmt(data.today?.salesAmount)}
                sub="Total billed amount"
                color="#198754"
                icon="💰"
              />
            </div>
            <div className="col-6 col-xl-2">
              <StatTile
                label="Cash received"
                value={fmt(data.today?.cashAmount)}
                sub="Cash sales today"
                color="#0891b2"
                icon="💵"
              />
            </div>
            <div className="col-6 col-xl-2">
              <StatTile
                label="EMI down pmts"
                value={fmt(data.today?.emiDownPayment)}
                sub="50% collected today"
                color="#7c3aed"
                icon="📥"
              />
            </div>
            <div className="col-6 col-xl-2">
              <StatTile
                label="EMI collected"
                value={fmt(data.today?.emiCollectedToday)}
                sub="Installments today"
                color="#b45309"
                icon="📊"
              />
            </div>
            <div className="col-6 col-xl-2">
              <StatTile
                label="My commission"
                value={fmt(data.commissionToday)}
                sub={`Month: ${fmt(data.commissionMonth)}`}
                color="#d97706"
                icon="⭐"
              />
            </div>
          </div>

          {/* ─── Advanced Analysis ─── */}
          <div className="section-head mb-0">Advanced Analysis</div>
          <div className="section-body mb-4">
            <div className="row g-3">
              <div className="col-md-4">
                <div className="analysis-card">
                  <div className="analysis-title">Current availability</div>
                  <div className="analysis-value">{data.stock?.totalItems || 0} units</div>
                  <div className="analysis-sub">
                    {data.stock?.totalProducts || 0} SKUs · {analysis.stockPerActiveEmi} stock/active EMI
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="analysis-card">
                  <div className="analysis-title">EMI portfolio load</div>
                  <div className="analysis-value">{data.pendingEmiCollections || 0} active</div>
                  <div className="analysis-sub">
                    Overdue {data.overdueEmiCount || 0} ({analysis.emiRiskPct}% risk ratio)
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="analysis-card">
                  <div className="analysis-title">Cash visibility</div>
                  <div className="analysis-value">{fmt(data.today?.totalCashToday)}</div>
                  <div className="analysis-sub">
                    Recovery share {analysis.cashFromRecoveryPct}% · Commission {fmt(data.commissionToday)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ─── Cash summary + EMI summary ─── */}
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <div className="section-head blue">Cash Flow Summary</div>
              <div className="section-body">
                <div className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <span>Cash sales</span>
                  <strong className="text-success">{fmt(data.today?.cashAmount)}</strong>
                </div>
                <div className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <span>EMI down payments</span>
                  <strong style={{ color: '#7c3aed' }}>{fmt(data.today?.emiDownPayment)}</strong>
                </div>
                <div className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <span>EMI installments collected</span>
                  <strong style={{ color: '#b45309' }}>{fmt(data.today?.emiCollectedToday)}</strong>
                </div>
                <div className="d-flex justify-content-between align-items-center pt-2">
                  <span className="fw-700">Total cash in hand today</span>
                  <strong className="text-success" style={{ fontSize: 16 }}>{fmt(data.today?.totalCashToday)}</strong>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="section-head" style={{ background: data.overdueEmiCount > 0 ? '#b91c1c' : '#198754' }}>
                EMI Status
                {data.overdueEmiCount > 0 && (
                  <span className="ms-2 fw-400" style={{ fontSize: 12 }}>⚠ {data.overdueEmiCount} overdue</span>
                )}
              </div>
              <div className="section-body">
                <div className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <span>Active EMI accounts</span>
                  <strong className="text-primary">{data.pendingEmiCollections || 0}</strong>
                </div>
                <div className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <span>Overdue accounts</span>
                  <strong style={{ color: data.overdueEmiCount > 0 ? '#b91c1c' : '#198754' }}>{data.overdueEmiCount || 0}</strong>
                </div>
                <div className="d-flex justify-content-between align-items-center pt-2">
                  <span>Stock items available</span>
                  <strong className="text-primary">{data.stock?.totalItems || 0}</strong>
                </div>
              </div>
            </div>
          </div>

          {/* ─── Overdue EMI cards ─── */}
          {data.topOverdue && data.topOverdue.length > 0 && (
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-700" style={{ color: '#b91c1c', fontSize: 15 }}>
                  ⚠ Overdue EMI Follow-up Required
                </div>
                <Link to="/emi" className="btn btn-sm btn-outline-danger">View all EMIs →</Link>
              </div>
              <div className="overdue-grid">
                {data.topOverdue.map(emi => (
                  <OverdueCard key={emi._id} emi={emi} />
                ))}
              </div>
            </div>
          )}

          {/* ─── My Earnings Journey (Commission Explainer + Today's Earnings) ─── */}
          {(() => {
            const currentMonth = commData?.timeline?.[commData.timeline.length - 1] || {};
            const unpaidBalance = commData?.summary?.unpaidConfirmed || 0;
            const instantToday = data.commissionToday || 0;
            const bonusPotential = currentMonth.bonusPotential5 || 0;
            const cashToSend = data.today?.totalCashToday || 0;
            const pendingHandover = data.pendingHandover || 0;
            const hasPendingHandover = pendingHandover > 0;

            return (
              <div className="row g-3 mb-4">
                {/* Earnings Journey Card */}
                <div className="col-lg-7">
                  <div className="card" style={{ border: '1px solid #e0e7ff', background: 'linear-gradient(135deg, #f5f3ff 0%, #eff6ff 100%)' }}>
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <div className="fw-700" style={{ fontSize: 15, color: '#4338ca' }}>⭐ Meri Kamai — How It Works</div>
                          <div className="text-muted" style={{ fontSize: 12 }}>Har sale pe earning hoti hai, step by step</div>
                        </div>
                        <Link to="/commission" style={{ fontSize: 12, color: '#4338ca', textDecoration: 'none', fontWeight: 600 }}>
                          Full details →
                        </Link>
                      </div>

                      {/* 3-step visual */}
                      <div className="d-flex gap-0 mb-3" style={{ position: 'relative' }}>
                        {[
                          {
                            step: '1',
                            title: 'Sale karo',
                            desc: 'Cash ya EMI koi bhi',
                            icon: '🛒',
                            color: '#0d6efd',
                            bg: '#eff6ff'
                          },
                          {
                            step: '2',
                            title: '2% Turant Mila',
                            desc: 'Abhi earn hua, redeem baad mein',
                            icon: '✅',
                            color: '#16a34a',
                            bg: '#f0fdf4'
                          },
                          {
                            step: '3',
                            title: '5% Upcoming Bonus',
                            desc: 'EMI collect karo, full bonus lo',
                            icon: '🎯',
                            color: '#7c3aed',
                            bg: '#f5f3ff'
                          }
                        ].map((s, i) => (
                          <React.Fragment key={s.step}>
                            <div style={{ flex: 1, background: s.bg, borderRadius: 8, padding: '10px 8px', textAlign: 'center' }}>
                              <div style={{ fontSize: 20 }}>{s.icon}</div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: s.color, marginTop: 2 }}>{s.title}</div>
                              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>{s.desc}</div>
                            </div>
                            {i < 2 && (
                              <div style={{ display: 'flex', alignItems: 'center', padding: '0 4px', color: '#9ca3af', fontSize: 16 }}>→</div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>

                      {/* Today's earnings summary */}
                      <div style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                        <div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>Aaj instant mili</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#16a34a' }}>{fmt(instantToday)}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>Is mahine ka bonus (best case)</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#7c3aed' }}>{fmt(bonusPotential)}</div>
                          <div style={{ fontSize: 10, color: '#9ca3af' }}>EMI puri hone ke baad milega</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>Total unpaid balance</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#b45309' }}>{fmt(unpaidBalance)}</div>
                          {unpaidBalance > 0 && (
                            <Link to="/commission" style={{ fontSize: 10, color: '#b45309', fontWeight: 600 }}>Request payout →</Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cash to Send Card */}
                <div className="col-lg-5">
                  <div className="card" style={{
                    border: `1px solid ${hasPendingHandover ? '#fde68a' : cashToSend > 1000 ? '#bbf7d0' : '#e5e7eb'}`,
                    background: hasPendingHandover ? '#fffbeb' : cashToSend > 1000 ? '#f0fdf4' : '#fff'
                  }}>
                    <div className="card-body p-3">
                      <div className="fw-700 mb-1" style={{ fontSize: 15, color: hasPendingHandover ? '#b45309' : '#1f2937' }}>
                        💵 Cash Transfer Status
                      </div>
                      <div className="text-muted mb-3" style={{ fontSize: 12 }}>Owner ko cash bhejo weekly ya jab ready ho</div>

                      <div style={{ borderRadius: 8, padding: '12px 14px', marginBottom: 12, background: '#f9fafb' }}>
                        <div className="d-flex justify-content-between mb-1">
                          <span style={{ fontSize: 12, color: '#6b7280' }}>Cash in hand today</span>
                          <strong style={{ color: '#1f2937' }}>{fmt(cashToSend)}</strong>
                        </div>
                        {hasPendingHandover && (
                          <div className="d-flex justify-content-between mb-1">
                            <span style={{ fontSize: 12, color: '#b45309' }}>⏳ Pending confirmation</span>
                            <strong style={{ color: '#b45309' }}>{fmt(pendingHandover)}</strong>
                          </div>
                        )}
                      </div>

                      {hasPendingHandover ? (
                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#92400e' }}>
                          ⏳ Aapne {fmt(pendingHandover)} bheja hai, owner se confirmation ka wait karo
                        </div>
                      ) : cashToSend > 0 ? (
                        <button
                          className="btn btn-success w-100"
                          style={{ fontWeight: 700 }}
                          onClick={() => navigate('/cash-handover')}
                        >
                          💸 {fmt(cashToSend)} Owner ko bhejo
                        </button>
                      ) : (
                        <button
                          className="btn btn-outline-secondary w-100"
                          onClick={() => navigate('/cash-handover')}
                        >
                          Cash Handover Record karo
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ─── Quick actions ─── */}
          <div>
            <div className="fw-700 mb-2" style={{ fontSize: 15 }}>Quick Actions</div>
            <div className="quick-actions-grid">
              {QUICK_ACTIONS.map(a => {
                const label = a.path === '/emi' && data.overdueEmiCount > 0
                  ? `Collect EMI (${data.overdueEmiCount} overdue)`
                  : a.label;
                return (
                  <ActionCard
                    key={a.path}
                    color={a.color}
                    label={label}
                    onClick={() => navigate(a.path)}
                  />
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
